import { useTheme, Grid } from '@mui/material';
import FullCalendar, { EventContentArg } from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { AgentSchedule } from '@nice-ccf/core-sdk';
import schedulerStyles from './ccf-scheduler.styles';
import {
  CcfTranslationKey,
  TranslatorContext,
  useTranslator,
} from '../../ccf-translator/ccf-translator';

/**
 * @remarks - AgentScheduleForDisplay extends agentsSchedule and adds few optional properties namely display, className, desccription,
 * borderHighlightColor for customization of events and shifts.
 */
export interface AgentScheduleForDisplay extends AgentSchedule {
  /**
   * @remarks - controls which preset rendering style events use, Possible values: auto, block, list-item, background, inverse-background, none
   */
  display?: string;
  /**
   * @remarks - determines which HTML classNames will be attached to the rendered event.
   */
  className?: string;
  /**
   * @remarks - shift/activity notes
   */
  description?: string;
  /**
   * @remarks - left-aligned highlighted activity color
   */
  borderHighlightColor?: string;
  /**
   * @remarks - shift/activity notes(max 500 characters)
   */
  isWEMEvent?: boolean;
}
export interface CcfSchedulerProps {
  events: AgentScheduleForDisplay[];
  initialView?: string;
  height?: string | number;
  nowIndicator?: boolean;
  headerToolbar?: any;
  dayMaxEvents?: number;
  slotDuration?: string;
  scrollTime?: string;
  slotMinTime?: string;
  firstDay?: number;
  allDaySlot?: boolean;
  allDayText?: CcfTranslationKey;
  isWemView?: boolean;
  /**
   * @remarks - renderScheduleContent used for rendering the event details
   **/
  renderScheduleContent?: (eventArgs: EventContentArg) => JSX.Element | '';
}

export const CcfScheduler = forwardRef((props: CcfSchedulerProps, ref: any) => {
  const [translator] = useTranslator();
  // Anchor the visible window to the earliest event's start (instead of the
  // wall clock) so the shift block always appears in the same visual spot.
  // slotMinTime = start − 1h, scrollTime = start − 30m, both computed from
  // the full Date (hours + minutes) and clamped to 00:00:00 so a shift that
  // begins shortly after midnight doesn't wrap to the previous day.
  const earliestEventStart = (props.events ?? []).reduce<Date | null>(
    (earliest, ev) => {
      if (!ev?.start) return earliest;
      const start = ev.start instanceof Date ? ev.start : new Date(ev.start);
      if (Number.isNaN(start.getTime())) return earliest;
      return !earliest || start < earliest ? start : earliest;
    },
    null
  );
  /**
   * Build a HH:mm:ss string `minsBefore` minutes earlier than `date`'s
   * time-of-day, clamped to 00:00:00 so it never wraps to the previous day.
   * @param date - reference Date (only its hours/minutes are read)
   * @param minsBefore - minutes to subtract from the time-of-day
   * @returns HH:mm:ss formatted string suitable for FullCalendar time props
   * @example minutesBeforeAsTimeString(new Date('2026-05-19T03:30:00'), 60) // '02:30:00'
   */
  const minutesBeforeAsTimeString = (date: Date, minsBefore: number): string => {
    const minsOfDay = Math.max(
      0,
      date.getHours() * 60 + date.getMinutes() - minsBefore
    );
    const h = Math.floor(minsOfDay / 60);
    const m = minsOfDay % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
  };
  const slotMinTimeAnchor = earliestEventStart
    ? minutesBeforeAsTimeString(earliestEventStart, 60)
    : '08:00:00';
  const scrollTimeAnchor = earliestEventStart
    ? minutesBeforeAsTimeString(earliestEventStart, 30)
    : '08:30:00';

  const {
    events,
    initialView = props.isWemView ? 'timeGridWeek' : 'timeGridDay',
    height = '100%',
    nowIndicator = true,
    headerToolbar = false,
    slotDuration = '01:00:00',
    scrollTime = scrollTimeAnchor,
    slotMinTime = slotMinTimeAnchor,
    firstDay = 0,
    allDaySlot = false,
    allDayText = 'shift',
    renderScheduleContent,
  } = props;
  const theme = useTheme();
  const styles = schedulerStyles(theme);
  const { locale } = useContext(TranslatorContext);

  const internalRef = useRef<FullCalendar>(null);
  useImperativeHandle(ref, () => internalRef.current);

  useEffect(() => {
    // Defer to next tick so FullCalendar finishes its initial layout before
    // we ask it to scroll — otherwise the call lands before the time grid
    // exists and the view stays at the day's start.
    const timer = setTimeout(() => {
      internalRef.current?.getApi()?.scrollToTime(scrollTime);
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid sx={styles.schedulerStyles}>
      <FullCalendar
        ref={internalRef}
        locale={locale}
        events={events}
        height={height}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={initialView}
        nowIndicator={nowIndicator}
        eventContent={renderScheduleContent}
        headerToolbar={headerToolbar}
        dayHeaderFormat={
          // !props?.isWemView
          //   ? {
          //       month: 'short',
          //       day: 'numeric',
          //       year: 'numeric',
          //     }
          //   :
          {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            omitCommas: true,
          }
        }
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'lowercase',
        }}
        slotLabelFormat={{
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: true,
          meridiem: 'lowercase',
        }}
        slotDuration={slotDuration}
        slotMinTime={slotMinTime}
        slotEventOverlap={false}
        scrollTime={scrollTime}
        firstDay={firstDay}
        eventTextColor="#001633"
        eventBorderColor="#dedede"
        allDaySlot={allDaySlot}
        allDayText={translator(allDayText)}
        eventOrderStrict={true}
        eventOrder={'start'}
      />
    </Grid>
  );
});
